import {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import Table from 'rsuite/Table'
import Tooltip from 'rsuite/Tooltip'
import Whisper from 'rsuite/Whisper'
import Pagination from 'rsuite/Pagination'
import IconButton from 'rsuite/esm/IconButton'
import {FiEye} from 'react-icons/fi'
import Edit2 from '@rsuite/icons/Edit'
import {toast} from 'react-toastify'
import {isEmpty} from 'lodash'
import {StateParamsModel} from 'src/app/modules/common/Model'
import CheckPermissions from 'src/app/modules/common/checkPermission'
import DesignComponent from 'src/app/modules/common/components/designComponent'
import RSuiteTable from 'src/cms/helpers/components/rsuiteTable'
import * as creditCardApplicationReportRedux from '../redux'
import {service} from '../redux/service'
import ViewCreditCardApplicationDetails from './ViewDetails'

const STATUS_OPTIONS = [
  {label: 'All', value: ''},
  {label: 'Incomplete', value: 'incomplete'},
  {label: 'Pending', value: 'pending'},
  {label: 'Approved', value: 'approved'},
  {label: 'Rejected', value: 'rejected'},
]

const CreditCardApplicationReport = () => {
  const dispatch = useDispatch()
  const {
    data: {data: applications, total},
    loading,
  } = useSelector((state: any) => state.creditCardApplicationReport)

  const [params, setParams] = useState<StateParamsModel>({page: 1, limit: 15})
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [showDetails, setShowDetails] = useState(false)
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [selectedStatus, setSelectedStatus] = useState('')
  const [updatingStatus, setUpdatingStatus] = useState(false)

  useEffect(() => {
    dispatch(creditCardApplicationReportRedux.actions.getCreditCardApplicationReport(params))
  }, [params])

  const Cell = Table.Cell

  const StatusBadge = ({status}: {status: string}) => {
    const colorMap: Record<string, string> = {
      incomplete: 'warning',
      pending: 'info',
      approved: 'success',
      rejected: 'danger',
    }
    return (
      <span className={`badge badge-light-${colorMap[status] ?? 'secondary'}`}>{status}</span>
    )
  }

  const ActionCell = ({rowData, ...props}: any) => (
    <Cell {...props} className='link-group'>
      <CheckPermissions type='Edit'>
        <Whisper placement='top' trigger='hover' speaker={<Tooltip>View Details</Tooltip>}>
          <IconButton
            appearance='subtle'
            icon={<FiEye />}
            onClick={() => {
              setSelectedId(rowData.id)
              setShowDetails(true)
            }}
          />
        </Whisper>
      </CheckPermissions>
      <CheckPermissions type='Edit'>
        <Whisper placement='top' trigger='hover' speaker={<Tooltip>Update Status</Tooltip>}>
          <IconButton
            appearance='subtle'
            icon={<Edit2 />}
            onClick={() => {
              setSelectedId(rowData.id)
              setSelectedStatus(rowData.application_status)
              setShowStatusModal(true)
            }}
          />
        </Whisper>
      </CheckPermissions>
    </Cell>
  )

  const columns = [
    {label: 'S.N.', dataKey: 'sn', width: 60, cell: <Cell dataKey='sn' />},
    {
      label: 'Request Code',
      dataKey: 'request_code',
      flexGrow: 1,
      cell: <Cell>{(rowData: any) => rowData.request_code || '-'}</Cell>,
    },
    {
      label: 'Applicant Name',
      dataKey: 'applicant_name',
      flexGrow: 1,
      cell: <Cell>{(rowData: any) => rowData.applicant_name || '-'}</Cell>,
    },
    {
      label: 'Mobile',
      dataKey: 'mobile_number',
      width: 130,
      cell: <Cell>{(rowData: any) => rowData.mobile_number || '-'}</Cell>,
    },
    {
      label: 'Step',
      dataKey: 'application_step',
      width: 90,
      cell: <Cell>{(rowData: any) => rowData.application_step || '-'}</Cell>,
    },
    {
      label: 'Status',
      dataKey: 'application_status',
      width: 130,
      cell: <Cell>{(rowData: any) => <StatusBadge status={rowData.application_status} />}</Cell>,
    },
    {label: 'Action', width: 100, align: 'center', cell: <ActionCell dataKey='id' />},
  ]

  const tableData = !isEmpty(applications)
    ? applications.map((item: any, i: number) => ({
        sn: (params.page - 1) * params.limit + (i + 1),
        id: item.id,
        request_code: item.request_code,
        application_step: item.application_step,
        application_status: item.application_status,
        mobile_number: item.general_information?.mobile_number,
        applicant_name: [item.general_information?.first_name, item.general_information?.last_name]
          .filter(Boolean)
          .join(' '),
      }))
    : []

  const handleRefresh = () => {
    dispatch(creditCardApplicationReportRedux.actions.getCreditCardApplicationReport(params))
  }

  const handleUpdateStatus = () => {
    if (!selectedId || !selectedStatus) return
    setUpdatingStatus(true)
    service
      .updateCreditCardApplicationStatus(selectedId, selectedStatus)
      .then(() => {
        toast.success('Status updated successfully.')
        setShowStatusModal(false)
        handleRefresh()
      })
      .catch(() => {
        toast.error('Failed to update status.')
      })
      .finally(() => setUpdatingStatus(false))
  }

  return (
    <>
      <div className='shadow p-3 bg-white rounded'>
        <DesignComponent
          moduleName='Credit Card Application Report'
          params={params}
          setParams={setParams}
          filterData={true}
          handleRefresh={handleRefresh}
          addShow={false}
          deleteShow={false}
          importShow={false}
          toggleMultipleShow={false}
          enableDropdownFilter={true}
          dropdownOption={STATUS_OPTIONS}
          handleDropdownChange={(option: any) => {
            setParams({...params, status: option?.value, page: 1})
          }}
          dropdownValue={
            STATUS_OPTIONS.find((o) => o.value === String(params.status ?? '')) ||
            STATUS_OPTIONS[0]
          }
          dropdownPlaceholder='Filter by Status'
        />

        <div className='datatable'>
          <RSuiteTable
            onChecked={() => {}}
            showCheckbox={false}
            columns={columns}
            showLoading={loading}
            data={tableData}
            checkedValues={[]}
          />
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            className='mt-5'
            maxButtons={5}
            size='sm'
            layout={['total', '-', 'limit', '|', 'pager', 'skip']}
            total={total || 0}
            limitOptions={[10, 20, 50]}
            limit={params.limit}
            activePage={params.page}
            onChangePage={(value) => setParams({...params, page: value})}
            onChangeLimit={(value) => setParams({...params, limit: value})}
          />
        </div>
      </div>

      {showDetails && selectedId && (
        <ViewCreditCardApplicationDetails id={selectedId} handleClose={() => setShowDetails(false)} />
      )}

      {showStatusModal && (
        <div className='modal show d-block' style={{background: 'rgba(0,0,0,0.5)'}}>
          <div className='modal-dialog modal-dialog-centered'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>Update Application Status</h5>
                <button className='btn-close' onClick={() => setShowStatusModal(false)} />
              </div>
              <div className='modal-body'>
                <label className='form-label fw-bold'>Status</label>
                <select
                  className='form-select'
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                >
                  <option value='incomplete'>Incomplete</option>
                  <option value='pending'>Pending</option>
                  <option value='approved'>Approved</option>
                  <option value='rejected'>Rejected</option>
                </select>
              </div>
              <div className='modal-footer'>
                <button className='btn btn-secondary btn-sm' onClick={() => setShowStatusModal(false)}>
                  Cancel
                </button>
                <button
                  className='btn btn-primary btn-sm'
                  onClick={handleUpdateStatus}
                  disabled={updatingStatus}
                >
                  {updatingStatus ? 'Updating...' : 'Update'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default CreditCardApplicationReport
